import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  MailOutlined,
  MobileOutlined,
} from '@ant-design/icons';
import {
  Circle,
  Heading,
  TextLink,
  EmailVerificationStatusIcon,
} from '@mm/components';
import { ENTER_KEY_NAME } from '@mm/constants/keys-codes';
import { useAttributeCRUDActions } from '@mm/state/actions/AttributeCRUDActions';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { identifyChannel } from '@mm/state/helpers/live-chat-helpers';
import { SelectedContact } from '@mm/state/interfaces/contacts';
import { LiveChatChannels } from '@mm/state/model/LivechatConversationModel';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import noProfilePicIcon from 'assets/icons/empty-avatar.svg';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ImageWithFallback } from '../../../../../../common/components/ImageWithFallback/ImageWithFallback';
import { colors } from '../../../../beta/styles/colors';
import { ChannelIcon } from '../../../../liveChat/components/ChannelIcon';
import { ReactComponent as BusinessIcon } from 'assets/icons/business-outlined.svg';
import { BUSINESS_EMAIL, BUSINESS_PHONE } from '../ContactDetails';
import './ContactDetailsHeader.pcss';
import { useResolutionInfo } from '@mm/hooks';
import { MOBILE_SMALL } from '../../../../../constants/screenSize';

type OuterProps = {
  deleteSelectedContact?: () => void;
  selectedContact: SelectedContact;
  onChangeContactInfo?: () => void;
  isLiveChatContacts?: boolean;
  collapsedContactView?: boolean;
  isBusinessContact?: boolean;
};

type Props = OuterProps;

// tslint:disable-next-line:cyclomatic-complexity
const ContactsDetailsHeader = (props: Props) => {
  const {
    selectedContact,
    deleteSelectedContact,
    onChangeContactInfo,
    isLiveChatContacts,
    collapsedContactView,
    isBusinessContact,
  } = props;

  const {
    fullName,
    profilePic,
    id: contactId,
    remoteId: remoteUserId,
    email,
    phone = '',
    location,
    unsubscribed,
    variables,
  } = selectedContact;

  const businessEmail = variables.find(
    variable => variable.variable.name === BUSINESS_EMAIL,
  )?.value;
  const businessPhone = variables.find(
    variable => variable.variable.name === BUSINESS_PHONE,
  )?.value;

  const [newEmailValue, setNewEmailValue] = useState(email || '');
  const [editEmail, setEditEmail] = useState(false);
  const [newPhoneValue, setNewPhoneValue] = useState(phone || '');
  const [editPhone, setEditPhone] = useState(false);
  const [newFullNameValue, setNewFullNameValue] = useState(fullName);
  const [editFullName, setEditFullName] = useState(false);

  const fullNameInputRef = useRef<HTMLInputElement>(null);

  const { addAttribute, deleteAttributeValue } = useAttributeCRUDActions();
  const { update: updateContact } = useContactsCRUDActions();
  const { screenWidth } = useResolutionInfo();
  const isSmallMobile = screenWidth <= MOBILE_SMALL;

  const fbPageId = useSelector(activeFacebookPageIdSelector);

  const isDummyContact = selectedContact.dummy;

  useEffect(() => {
    setEditEmail(false);
    setEditPhone(false);
    setEditFullName(false);
    setNewFullNameValue(fullName);
  }, [fullName, selectedContact.id]);

  const channel = identifyChannel(remoteUserId);

  const allowFullNameEditing = channel !== 'facebook';

  const editField = async (name: string, value: string) => {
    const valueText = value.trim();
    let response;
    if (!valueText) {
      const variable = selectedContact.variables.find(
        v => v.variable.name === name,
      );
      if (variable) {
        response = await deleteAttributeValue(variable.id);
      }
    } else {
      response = await addAttribute(contactId, name, valueText);
    }
    if (response && onChangeContactInfo) {
      onChangeContactInfo();
    }
    setEditEmail(false);
    setEditPhone(false);
  };
  const updateFullName = async () => {
    await updateContact(fbPageId, {
      id: selectedContact.id,
      fullName: newFullNameValue,
    });
    onChangeContactInfo?.();
    setEditFullName(false);
  };

  useEffect(() => {
    setNewEmailValue(email || '');
    setNewPhoneValue(phone || '');
  }, [email, phone]);

  const generateName = (selectedContact: SelectedContact) => {
    if (
      selectedContact.firstName &&
      selectedContact.lastName &&
      selectedContact.username
    ) {
      if (
        selectedContact.firstName === 'Instagram Contact' &&
        selectedContact.lastName === 'IG' &&
        selectedContact.username
      ) {
        return `@${selectedContact.username}`;
      }
      return `${selectedContact.firstName} ${selectedContact.lastName} (@${selectedContact.username})`;
    }
    if (selectedContact.firstName && selectedContact.lastName) {
      return `${selectedContact.firstName} ${selectedContact.lastName}`;
    }
    if (selectedContact.firstName) {
      return `${selectedContact.firstName}`;
    }
    if (selectedContact.lastName) {
      return `${selectedContact.lastName}`;
    }
    if (selectedContact.username) {
      return `@${selectedContact.username}`;
    }
    if (selectedContact.email) {
      return selectedContact.email;
    }
  };

  const emailInputComponent = editEmail ? (
    <div className="contact-details-header__info-item">
      {selectedContact.email ? (
        <EmailVerificationStatusIcon
          contact={selectedContact}
          className="contact-details-header__email-verification-icon"
        />
      ) : (
        <MailOutlined className="contact-details-header__email-verification-icon" />
      )}
      <input
        className="contact-details-header__input-container"
        value={newEmailValue}
        placeholder="Add email"
        onChange={event => setNewEmailValue(event.target.value)}
      />
      <div
        className={classNames(
          'contact-details-header__profile-name-input-button',
          {
            'contact-details-header__profile-name-input-button-disabled':
              newEmailValue.trim().length === 0,
          },
        )}
        onClick={() => editField('EMAIL', newEmailValue)}
      >
        <CheckOutlined className="contact-details-header__profile-name-input-button-check-mark" />
      </div>
      <CloseOutlined
        className="contact-details-header__cancel-btn"
        onClick={() => {
          setNewEmailValue(email || '');
          setEditEmail(false);
        }}
      />
    </div>
  ) : (
    <div className="contact-details-header__info-item">
      {selectedContact.email ? (
        <EmailVerificationStatusIcon
          contact={selectedContact}
          className="contact-details-header__email-verification-icon"
        />
      ) : (
        <MailOutlined className="contact-details-header__email-verification-icon" />
      )}
      {newEmailValue ? (
        <>
          {newEmailValue}{' '}
          {isBusinessContact && newEmailValue.includes(businessEmail) && (
            <div
              className={classNames('contact-details-header__business-lead', {
                'contact-details-header__business-lead-mobile': isSmallMobile,
              })}
            >
              <BusinessIcon className="contact-details-header__business-icon" />
              {!isSmallMobile && 'Business Email'}
            </div>
          )}
        </>
      ) : (
        <span className="contact-details-header__placeholder">none</span>
      )}

      {!isDummyContact && (
        <EditOutlined
          className="contact-details-header__edit-icon"
          onClick={() => setEditEmail(true)}
        />
      )}
    </div>
  );

  const phoneInputComponent = editPhone ? (
    <div className="contact-details-header__info-item">
      <MobileOutlined className="contact-details-header__info-icon" />
      <input
        className="contact-details-header__input-container"
        value={newPhoneValue}
        placeholder="Add phone number"
        onChange={event => setNewPhoneValue(event.target.value)}
      />
      <div
        className={classNames(
          'contact-details-header__profile-name-input-button',
          {
            'contact-details-header__profile-name-input-button-disabled':
              newPhoneValue.trim().length === 0,
          },
        )}
        onClick={() => editField('PHONE', newPhoneValue)}
      >
        <CheckOutlined className="contact-details-header__profile-name-input-button-check-mark" />
      </div>
      <CloseOutlined
        className="contact-details-header__cancel-btn"
        onClick={() => {
          setNewPhoneValue(phone || '');
          setEditPhone(false);
        }}
      />
    </div>
  ) : (
    <div className="contact-details-header__info-item">
      <MobileOutlined className="contact-details-header__info-icon" />
      {newPhoneValue ? (
        newPhoneValue
      ) : (
        <span className="contact-details-header__placeholder">none</span>
      )}
      {isBusinessContact && newPhoneValue.includes(businessPhone) && (
        <div
          className={classNames('contact-details-header__business-lead', {
            'contact-details-header__business-lead-mobile': isSmallMobile,
          })}
        >
          <BusinessIcon className="contact-details-header__business-icon" />
          {!isSmallMobile && 'Business Phone'}
        </div>
      )}
      {!isDummyContact && (
        <EditOutlined
          className="contact-details-header__edit-icon"
          onClick={() => setEditPhone(true)}
        />
      )}
    </div>
  );

  const fullNameInputComponent = editFullName ? (
    <>
      <input
        ref={fullNameInputRef}
        className="contact-details-header__profile-name-input"
        value={newFullNameValue}
        placeholder="First Name, Last Name"
        onChange={event => setNewFullNameValue(event.target.value)}
        onKeyDown={event => {
          if (event.key === ENTER_KEY_NAME) {
            updateFullName();
          }
        }}
      />

      <div
        className={classNames(
          'contact-details-header__profile-name-input-button',
          {
            'contact-details-header__profile-name-input-button-disabled':
              newFullNameValue.trim().length === 0,
          },
        )}
        onClick={updateFullName}
      >
        <CheckOutlined className="contact-details-header__profile-name-input-button-check-mark" />
      </div>
      <CloseOutlined
        className="contact-details-header__cancel-btn"
        onClick={() => {
          setNewFullNameValue(fullName);
          setEditFullName(false);
        }}
      />
    </>
  ) : (
    <>
      <Heading
        size={3}
        text={generateName(selectedContact)}
        className="contact-details-header__profile-name"
      />
      {allowFullNameEditing && !editFullName && !isDummyContact && (
        <EditOutlined
          className="contact-details-header__edit-icon"
          onClick={() => {
            setEditFullName(true);
            setTimeout(() => fullNameInputRef.current?.focus(), 50);
          }}
        />
      )}
    </>
  );

  const profilePicture = (
    <ImageWithFallback
      src={profilePic}
      fallbackImage={noProfilePicIcon}
      className="contact-details-header__profile-pic-image"
      alt={fullName}
    />
  );

  if (collapsedContactView) {
    return (
      <div className="contact-details-header--collapsed">
        <div className="contact-details-header__profile-pic-container">
          {profilePicture}
        </div>
      </div>
    );
  }

  const showSubscribedIndicator =
    !isLiveChatContacts && unsubscribed !== undefined;

  return (
    <div className="contact-details-header">
      <div className="contact-details-header__topbar">
        <div
          className={classNames('contact-details-header__profile-pic', {
            'contact-details-header__profile-pic-mobile': isSmallMobile,
          })}
        >
          {profilePicture}
          {isSmallMobile && (
            <div className="contact-details-header__action-buttons">
              {deleteSelectedContact && !isDummyContact && (
                <DeleteOutlined
                  className="contact-details-header__action-btn"
                  onClick={deleteSelectedContact}
                />
              )}
            </div>
          )}
        </div>

        <div className="contact-details-header__content">
          <div className="contact-details-header__profile-name-container">
            {fullNameInputComponent}
            {isBusinessContact && (
              <div
                className={classNames('contact-details-header__business-lead', {
                  'contact-details-header__business-lead-mobile': isSmallMobile,
                })}
              >
                <BusinessIcon className="contact-details-header__business-icon" />
                {!isSmallMobile && 'Business Lead'}
              </div>
            )}
            {showSubscribedIndicator && (
              <div className="contact-details-header__profile-subscribed-indicator">
                <Circle
                  className="contact-details-header__profile-subscribed-indicator-badge"
                  color={
                    unsubscribed ? colors.grayLight : 'rgba(82, 196, 26, 1)'
                  }
                />
                <TextLink className="contact-details-header__profile-subscribed-indicator-text">
                  {unsubscribed ? 'Unsubscribed' : 'Subscribed'}
                </TextLink>
              </div>
            )}
          </div>
          {!selectedContact.loading && (
            <>
              <div className="contact-details-header__contact-channel-icon-container">
                <span className="contact-details-header__info-icon">
                  <ChannelIcon remoteUserId={remoteUserId} />
                </span>
                <span className="contact-details-header__contact-channel-name">
                  {LiveChatChannels[channel]}
                </span>
              </div>
              {emailInputComponent}
              {phoneInputComponent}
              {location && (
                <div className="contact-details-header__info-item">
                  <EnvironmentOutlined className="contact-details-header__info-icon" />
                  {location}
                </div>
              )}
            </>
          )}
        </div>
        {!isSmallMobile && (
          <div className="contact-details-header__action-buttons">
            {deleteSelectedContact && !isDummyContact && (
              <DeleteOutlined
                className="contact-details-header__action-btn"
                onClick={deleteSelectedContact}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactsDetailsHeader;
